import { forwardRef, useEffect, useImperativeHandle, useRef } from "react"

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
const SCRIPT_ID = "cf-turnstile-script"

export interface TurnstileWidgetHandle {
  reset: () => void
}

interface TurnstileWidgetProps {
  /** Passed to Cloudflare for analytics segmentation (e.g. "login"). */
  action?: string
  /** Called with the one-time token once the challenge has been solved. */
  onToken: (token: string) => void
  /** Called when token expires, errors, or times out — parent should clear its token state. */
  onReset?: () => void
  /** When true, passes `refresh-expired: auto` so Cloudflare silently re-runs expired challenges. */
  autoRefresh?: boolean
}

/**
 * Cloudflare Turnstile widget (anti-bot).
 *
 * - Callbacks are stored in refs so the widget is never re-mounted when parent re-renders.
 * - Exposes a `reset()` handle via ref so the parent can refresh the challenge after a failed
 *   submission (tokens are single-use).
 * - Renders nothing when `REACT_APP_TURNSTILE_SITE_KEY` is not configured (graceful degradation).
 */
export const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  ({ action = "login", onToken, onReset, autoRefresh = false }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<string | null>(null)
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY

    // Store callbacks in refs so the widget is never re-mounted due to prop changes
    const onTokenRef = useRef(onToken)
    const onResetRef = useRef(onReset)
    onTokenRef.current = onToken
    onResetRef.current = onReset

    useImperativeHandle(ref, () => ({
      reset() {
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.reset(widgetIdRef.current)
        }
      }
    }))

    useEffect(() => {
      if (!siteKey) return

      const renderWidget = () => {
        if (!containerRef.current || widgetIdRef.current !== null || !window.turnstile) return

        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          action,
          appearance: "always",
          callback: (token: string) => onTokenRef.current(token),
          "expired-callback": () => onResetRef.current?.(),
          "error-callback": () => onResetRef.current?.(),
          "timeout-callback": () => onResetRef.current?.(),
          ...(autoRefresh ? { "refresh-expired": "auto" } : {})
        })
      }

      if (window.turnstile) {
        renderWidget()
      } else if (!document.getElementById(SCRIPT_ID)) {
        const script = document.createElement("script")
        script.id = SCRIPT_ID
        script.src = SCRIPT_URL
        script.async = true
        script.defer = true
        script.onload = renderWidget
        document.head.appendChild(script)
      } else {
        const interval = setInterval(() => {
          if (window.turnstile) {
            clearInterval(interval)
            renderWidget()
          }
        }, 100)
        return () => clearInterval(interval)
      }

      return () => {
        if (widgetIdRef.current !== null && window.turnstile) {
          window.turnstile.remove(widgetIdRef.current)
          widgetIdRef.current = null
        }
      }
    }, [siteKey])

    if (!siteKey) return null

    return <div ref={containerRef} />
  }
)

TurnstileWidget.displayName = "TurnstileWidget"
